import { useRef, useState } from 'react';
import type { CsvColumn } from '@/lib/csv';
import { toCsv, templateCsv, parseCsv, download } from '@/lib/csv';
import type { BulkResult } from '@/lib/types';

interface Props {
  /** File-name stem, e.g. "students" -> students-template.csv. */
  entity: string;
  /** Plural label for messages, e.g. "Students". */
  label: string;
  columns: CsvColumn[];
  /** Current records used for Export (raw API rows). */
  rows: Record<string, unknown>[];
  /** Send parsed rows to the API; resolves to a created/failed summary. */
  onImport: (rows: Record<string, string>[]) => Promise<BulkResult>;
  /** Called after a successful import so the list can reload. */
  onDone: () => void;
}

const btn = 'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-[#E1E5EA] bg-white text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50';

export default function ImportExport({ entity, label, columns, rows, onImport, onDone }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const stamp = new Date().toISOString().slice(0, 10);

  const handleTemplate = () => download(`${entity}-template.csv`, templateCsv(columns));

  const handleExport = () => {
    setMessage(''); setError('');
    if (rows.length === 0) { setError(`No ${label.toLowerCase()} to export yet.`); return; }
    download(`${entity}-${stamp}.csv`, toCsv(columns, rows));
  };

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setMessage(''); setError(''); setResult(null);
    let text = '';
    try { text = await file.text(); } catch { setError('Could not read that file.'); return; }
    const parsed = parseCsv(text, columns);
    if (parsed.length === 0) { setError('No data rows found. Download the template and match its column headers.'); return; }
    setBusy(true);
    try {
      const summary = await onImport(parsed);
      setResult(summary);
      if (summary.created > 0) onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button type="button" onClick={handleTemplate} className={btn} title={`Download a sample ${label} CSV template`}>
          <i className="ri-file-text-line text-base" /> Template
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={btn}>
          <i className="ri-upload-2-line text-base" /> {busy ? 'Importing…' : 'Import'}
        </button>
        <button type="button" onClick={handleExport} className={btn}>
          <i className="ri-download-2-line text-base" /> Export
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
      </div>

      {error && <div className="fixed inset-0 z-50 flex items-center justify-center p-4"><div className="absolute inset-0 bg-black/40" onClick={() => setError('')} /><div className="relative w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 text-center"><div className="w-11 h-11 mx-auto rounded-full bg-red-50 text-red-500 flex items-center justify-center"><i className="ri-error-warning-line text-xl" /></div><p className="mt-3 text-sm text-gray-700">{error}</p><button type="button" onClick={() => setError('')} className="mt-4 px-4 py-2 bg-[#0B6E77] text-white rounded-lg text-sm cursor-pointer">Got it</button></div></div>}

      {message && <div className="fixed bottom-4 right-4 z-50 bg-[#0B6E77] text-white text-sm px-4 py-2 rounded-lg shadow-lg">{message}</div>}

      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setResult(null)} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-6 py-5 border-b border-[#E1E5EA] flex-shrink-0">
              <h3 className="font-bold text-[#3C3C3C]">Import complete</h3>
              <p className="text-sm text-gray-500 mt-1">
                <span className="font-semibold text-green-600">{result.created} added</span>
                {result.failed.length > 0 && <> · <span className="font-semibold text-red-600">{result.failed.length} skipped</span></>}
              </p>
            </div>
            {result.failed.length > 0 && (
              <div className="p-5 overflow-y-auto">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Skipped rows</p>
                <ul className="space-y-1.5">
                  {result.failed.slice(0, 50).map((f, idx) => (
                    <li key={idx} className="text-xs text-gray-600"><span className="font-semibold text-[#3C3C3C]">Row {f.row}:</span> {f.error}</li>
                  ))}
                </ul>
                {result.failed.length > 50 && <p className="text-xs text-gray-400 mt-2">…and {result.failed.length - 50} more.</p>}
              </div>
            )}
            <div className="px-6 py-4 bg-gray-50 flex justify-end flex-shrink-0 border-t border-[#E1E5EA]">
              <button type="button" onClick={() => setResult(null)} className="px-5 py-2 bg-[#0B6E77] text-white rounded-lg text-sm font-semibold cursor-pointer">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
